import { Box, Typography, Alert, Card, CardContent, Divider, Chip, List, ListItem, ListItemText } from "@mui/material";
import type { Job } from "../../../../../../models";
import type { WizardIssues } from "../WizardContainer";

// ============================================================================
// Types
// ============================================================================

export interface SummaryProps {
	jobs: Job[];
	issues: WizardIssues;
	skippedIssueIds: Set<string>;
}

// ============================================================================
// Component
// ============================================================================

export function Summary({ jobs, issues, skippedIssueIds }: SummaryProps) {
	// Calculate summary stats
	const totalIssues =
		issues.overlaps.length +
		issues.duplicateBoundaries.length +
		issues.duplicatePointsZones.length +
		issues.unmatched.length +
		issues.invalidData.length;

	const skippedCount = skippedIssueIds.size;
	const remainingIssues = totalIssues - skippedCount;

	// Group jobs by grower
	const jobsByGrower = jobs.reduce(
		(acc, job) => {
			const growerName = job.growerName || job.importGrowerName || "Unknown Grower";
			if (!acc[growerName]) {
				acc[growerName] = [];
			}
			acc[growerName].push(job);
			return acc;
		},
		{} as Record<string, Job[]>,
	);

	return (
		<Box>
			{/* Stats Cards */}
			<Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
				<Card sx={{ minWidth: 150 }}>
					<CardContent>
						<Typography variant="h4" color="primary" data-testid="jobs-count">
							{jobs.length}
						</Typography>
						<Typography variant="body2" color="text.secondary">
							Total Jobs
						</Typography>
					</CardContent>
				</Card>

				<Card sx={{ minWidth: 150 }}>
					<CardContent>
						<Typography variant="h4" color="success.main" data-testid="skipped-count">
							{skippedCount}
						</Typography>
						<Typography variant="body2" color="text.secondary">
							Issues Skipped
						</Typography>
					</CardContent>
				</Card>

				{remainingIssues > 0 && (
					<Card sx={{ minWidth: 150 }}>
						<CardContent>
							<Typography variant="h4" color="warning.main" data-testid="remaining-count">
								{remainingIssues}
							</Typography>
							<Typography variant="body2" color="text.secondary">
								Remaining Issues
							</Typography>
						</CardContent>
					</Card>
				)}
			</Box>

			<Divider sx={{ my: 2 }} />

			{/* Jobs by Grower */}
			<Typography variant="subtitle1" gutterBottom>
				Jobs by Grower
			</Typography>

			<Box
				sx={{
					display: "grid",
					gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
					gap: 2,
				}}
			>
				{Object.entries(jobsByGrower).map(([growerName, growerJobs]) => (
					<Card key={growerName} sx={{ height: "100%" }}>
						<CardContent sx={{ py: 1, "&:last-child": { pb: 1 } }}>
							<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
								<Typography variant="body1" fontWeight="medium">
									{growerName}
								</Typography>
								<Chip label={`${growerJobs.length} job${growerJobs.length !== 1 ? "s" : ""}`} size="small" />
							</Box>
							<List dense sx={{ py: 0 }}>
								{growerJobs.slice(0, 3).map((job) => (
									<ListItem key={job.id} sx={{ py: 0, px: 0 }}>
										<ListItemText
											primary={job.fieldName || job.importFieldName || "Unknown Field"}
											secondary={job.farmName || job.importFarmName || "Unknown Farm"}
											primaryTypographyProps={{ variant: "body2" }}
											secondaryTypographyProps={{ variant: "caption" }}
										/>
									</ListItem>
								))}
								{growerJobs.length > 3 && (
									<ListItem sx={{ py: 0, px: 0 }}>
										<ListItemText
											primary={`... and ${growerJobs.length - 3} more`}
											primaryTypographyProps={{ variant: "caption", color: "text.secondary" }}
										/>
									</ListItem>
								)}
							</List>
						</CardContent>
					</Card>
				))}
			</Box>

			{jobs.length === 0 && (
				<Alert severity="info" sx={{ mt: 2 }}>
					<Typography>No jobs to load. All jobs may have been deleted during issue resolution.</Typography>
				</Alert>
			)}
		</Box>
	);
}
